<script setup>
import { computed, ref } from 'vue';
import { OnClickOutside } from '@vueuse/components';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  options: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: 'Select',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const isOpen = ref(false);

const normalizedValue = computed(() => String(props.modelValue || ''));
const selectedOption = computed(() =>
  props.options.find(
    option => String(option.value || '') === normalizedValue.value
  )
);

const buttonLabel = computed(() => {
  if (props.loading) return 'Loading...';
  return selectedOption.value?.label || props.placeholder;
});

const close = () => {
  isOpen.value = false;
};

const toggle = () => {
  if (props.disabled || props.loading) return;
  isOpen.value = !isOpen.value;
};

const selectOption = option => {
  if (option.disabled) return;
  emit('update:modelValue', String(option.value || ''));
  close();
};
</script>

<template>
  <OnClickOutside @trigger="close">
    <div class="relative w-full">
      <button
        type="button"
        class="flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-start text-sm text-n-slate-12 outline-none transition hover:border-n-slate-6 focus:border-n-brand focus:shadow-[0_0_0_3px_rgba(31,147,255,0.16)] disabled:cursor-not-allowed disabled:bg-n-alpha-2 disabled:text-n-slate-10"
        :disabled="disabled || loading"
        @click="toggle"
      >
        <span class="flex min-w-0 items-center gap-2">
          <span
            v-if="selectedOption?.dot"
            class="size-2 shrink-0 rounded-full"
            :style="{ backgroundColor: selectedOption.dot }"
          />
          <span class="truncate">{{ buttonLabel }}</span>
        </span>
        <span class="i-lucide-chevron-down size-4 shrink-0 text-n-slate-10" />
      </button>

      <div
        v-if="isOpen"
        class="absolute left-0 top-[calc(100%+4px)] z-50 max-h-56 w-full overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 p-1 shadow-xl"
      >
        <button
          v-for="option in options"
          :key="`${option.value}-${option.label}`"
          type="button"
          class="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-start text-sm text-n-slate-12 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:text-n-slate-9"
          :disabled="option.disabled"
          @click="selectOption(option)"
        >
          <span
            v-if="option.dot"
            class="size-2 shrink-0 rounded-full"
            :style="{ backgroundColor: option.dot }"
          />
          <span class="min-w-0 flex-1 truncate">{{ option.label }}</span>
          <span
            v-if="String(option.value || '') === normalizedValue"
            class="i-lucide-check size-4 shrink-0 text-n-brand"
          />
        </button>
      </div>
    </div>
  </OnClickOutside>
</template>
